// Angular
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// RxJS
import { Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, finalize, mergeMap, skip, switchMap, takeUntil, tap } from 'rxjs/operators';
// Translate
import { TranslateService } from '@ngx-translate/core';
// Store
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../core/reducers';
// Auth
import {
	AuthNoticeService,
	AuthService,
	isLoggedIn,
	isUserLoaded,
	Login,
	User
} from '../../../../core/auth';

import * as CryptoJS from 'crypto-js';
import { selectAuthState } from '../../../../core/auth/_selectors/auth.selectors';
import { CompanyService } from '../../../../core/auth/_services/company.service';
import { PlantService } from '../../../../core/plant-configuration/_services/plant.service';
import { ModuleService } from '../../../../core/plant-configuration/_services/module.service';


/**
 * ! Just example => Should be removed in development
 */
const DEMO_PARAMS = {
	EMAIL: 'admin@ballerina.cl',
	PASSWORD: ''
};

@Component({
	selector: 'kt-login',
	templateUrl: './login.component.html',
	encapsulation: ViewEncapsulation.None
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {
	// Public params
	loginForm: FormGroup = this.fb.group({
		email: [DEMO_PARAMS.EMAIL, Validators.compose([
			Validators.required,
			Validators.email,
			Validators.minLength(3),
			Validators.maxLength(320) // https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
		])
		],
		password: [DEMO_PARAMS.PASSWORD, Validators.compose([
			Validators.required,
			Validators.minLength(3),
			Validators.maxLength(100)
		])
		],
		remember: [false]
	});
	loading = false;
	isLoggedIn$: Observable<boolean>;
	errors: any = [];

	private unsubscribe: Subject<any>;

	private returnUrl: any;

	// Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

	/**
	 * Component constructor
	 *
	 * @param router: Router
	 * @param auth: AuthService
	 * @param authNoticeService: AuthNoticeService
	 * @param translate: TranslateService
	 * @param store: Store<AppState>
	 * @param fb: FormBuilder
	 * @param cdr
	 * @param route
	 */
	constructor(
		private router: Router,
		private auth: AuthService,
		private authNoticeService: AuthNoticeService,
		private plantService: PlantService,
		private translate: TranslateService,
		private store: Store<AppState>,
		private fb: FormBuilder,
		private cdr: ChangeDetectorRef,
		private route: ActivatedRoute,
		private companyService: CompanyService,
		private moduleService: ModuleService,
		private authService: AuthService
	) {
		this.unsubscribe = new Subject();
	}

	/**
	 * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
	 */

	/**
	 * On init
	 */
	ngOnInit(): void {
		this.initLoginForm();

		// redirect back to the returnUrl before login
		this.route.queryParams.subscribe(params => {
			this.returnUrl = params.returnUrl || '/';
		});

		this.store.pipe(
			select(selectAuthState),
			skip(1),
			distinctUntilChanged()
		).subscribe((auth) => {
			//console.log("auth state: "+JSON.stringify(auth));
			if(auth.isUserLoaded)
				this.router.navigateByUrl(this.returnUrl); // Main page
			else
				console.log("login component: usuario no logueado");
		});
	}

	ngAfterViewInit(): void {
		if(this.authService.isAuthenticated()){
			// this.navController.navigateBack(['app/mapas']);
			//console.log("AUTH SERVICE DICE QUE ESTA AUTENTICADO => RUTEA")
			const user = this.authService.getUser();
			const plant = this.authService.getPlant();
			const token = this.authService.getToken();
			const company = {
				id: user.company_id,
				name: user.company_name,
				theme_name: user['company_theme_name'],
				icon_file: user['icon_file'],
				icon_filename: user['icon_filename']
			};

			this.plantService.setCurrentPlant(plant);
			this.cdr.detectChanges();
			this.companyService.setCurrentCompany(company);
			this.store.dispatch(new Login({authToken: token, user: user}));
			// this.router.navigate([this.returnUrl])
			this.moduleService.getAll().subscribe(()=>this.router.navigateByUrl(this.returnUrl));
		}else if(this.authService.doesRememberLogin()){
			//console.log("AUTH SERVICE DICE QUE RECUERDA LOGIN => PATCH FORM")
			this.loginForm.controls.email.patchValue(this.authService.rememberLastUserLogin());
			this.loginForm.controls.remember.patchValue(true);
			this.cdr.detectChanges(); // Asegura render visual
			// console.log("DOES REMEMBER", this.loginForm)
		}else{
			//console.log("todo mal :(")
		}
	}

	/**
	 * On destroy
	 */
	ngOnDestroy(): void {
		this.authNoticeService.setNotice(null);
		this.unsubscribe.next();
		this.unsubscribe.complete();
		this.loading = false;
	}

	/**
	 * Form initalization
	 * Default params, validators
	 */
	initLoginForm() {
		// demo message to show
		if (!this.authNoticeService.onNoticeChanged$.getValue()) {
			const initialNotice = `Use account
			<strong>${DEMO_PARAMS.EMAIL}</strong> and password
			<strong>${DEMO_PARAMS.PASSWORD}</strong> to continue.`;
			this.authNoticeService.setNotice(initialNotice, 'info');
		}
	}

	/**
	 * Form Submit
	 */
	submit() {
		const controls = this.loginForm.controls;
		/** check form */
		if (this.loginForm.invalid) {
			Object.keys(controls).forEach(controlName =>
				controls[controlName].markAsTouched()
			);
			return;
		}

		this.loading = true;
		var password_crypt;
		try {
			password_crypt = CryptoJS.MD5(controls.password.value).toString();;
		} catch (e) {
			console.log(e);
		}
		const authData = {
			email: controls.email.value,
			password: password_crypt
		};
		this.auth
			.login(authData.email, authData.password)
			.pipe(
				tap(rsp => {
					console.log("login in login component");
					console.dir(rsp);
					if(rsp != null && rsp.user){
						const user:User = rsp.user;
						const token:string = rsp.token;
						const plant = {id: rsp.plant_id, name: rsp.plant_name};
						this.authService.setToken(token);
						this.authService.setUser(user);
						this.authService.setPlant(plant);
						if(this.loginForm.controls.remember.value){
							this.authService.markForRemember();
						}else{
							this.authService.markForNotRemember();
						}
						this.authService.authenticated$.next(true);
						localStorage.setItem("uid", user.id.toString());
                        localStorage.setItem("group_id", user.group_id.toString());
						localStorage.setItem("partner_id", (user.partner_id?user.partner_id:0).toString());
						this.store.dispatch(new Login({authToken: token, user: user}));
						// this.auth.toggleUsers(authData.email).subscribe(res => {
						// 	console.log(res);
						// });

						const company = {
							id: user.company_id,
							name: user.company_name,
							theme_name: user['company_theme_name'],
							icon_file: user['icon_file'],
							icon_filename: user['icon_filename']
						};
						this.plantService.setCurrentPlant(plant);
						this.cdr.detectChanges();
						this.companyService.setCurrentCompany(company);
						// this.router.navigateByUrl(this.returnUrl); // Main page

						// of(null).pipe(
						// 	switchMap(()=>this.companyService.getAll())
						// ).subscribe((companyList)=>{
						// 	// console.log("usuario");console.dir(user);
						// 	const company = companyList.find(company=>company.id==user.company_id);
						// 	this.cdr.detectChanges();
						// 	this.companyService.setCurrentCompany(company);
						// 	this.router.navigateByUrl(this.returnUrl); // Main page
						// });

					} else {
						this.authNoticeService.setNotice(this.translate.instant('AUTH.VALIDATION.INVALID_LOGIN'), 'danger');
					}
				}),
				filter((rsp:any)=>(rsp != null && rsp.user)),
				switchMap(()=>this.moduleService.getAll()),
				tap(()=>this.router.navigateByUrl(this.returnUrl)),
				takeUntil(this.unsubscribe),
				finalize(() => {
					this.loading = false;
					this.cdr.markForCheck();
				})
			)
			.subscribe();
	}

	/**
	 * Checking control validation
	 *
	 * @param controlName: string => Equals to formControlName
	 * @param validationType: string => Equals to valitors name
	 */
	isControlHasError(controlName: string, validationType: string): boolean {
		const control = this.loginForm.controls[controlName];
		if (!control) {
			return false;
		}

		const result = control.hasError(validationType) && (control.dirty || control.touched);
		return result;
	}
}
